/* ============================================================
   Wido Ops — Face-recognition device ID mapping.
   The backend auto-fetches (id, name) enrollments from each
   terminal. Employee-centric: each employee row has a searchable
   dropdown per device to pick their synced entry.
   Exports window.DeviceMappingScreen.
   ============================================================ */
(function () {
  const { useState } = React;
  const D = window.WidoData;
  const PersonCell = window.HRPersonCell;

  // --- searchable dropdown: pick a device enrollment entry for one employee+device ---
  function EntryPicker({ value, options, onChange, conflict }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [pos, setPos] = useState(null);
    const btnRef = React.useRef(null);
    const sel = options.find((o) => o.extId === value);
    const filtered = q ? options.filter((o) => o.extId.toLowerCase().includes(q.toLowerCase()) || (o.name || "").toLowerCase().includes(q.toLowerCase())) : options;
    // Open as a viewport-anchored FIXED popover so the menu isn't clipped at the
    // bottom of the table — flips upward when there's no room below, clamps left.
    function openMenu() {
      const r = btnRef.current && btnRef.current.getBoundingClientRect();
      if (r) {
        const W = 280, vh = window.innerHeight;
        const below = vh - r.bottom;
        const left = Math.min(r.left, window.innerWidth - W - 12);
        const up = below < 320 && r.top > below;   // open upward if not enough room below
        setPos({ left: Math.max(12, left), top: up ? null : r.bottom + 6, bottom: up ? (vh - r.top + 6) : null, width: Math.max(W, r.width), maxH: Math.max(220, (up ? r.top : below) - 24) });
      }
      setOpen(true); setQ("");
    }
    return React.createElement("div", { style: { position: "relative" } },
      React.createElement("button", {
        ref: btnRef,
        className: "gaccount", onClick: () => (open ? setOpen(false) : openMenu()),
        style: { width: "100%", padding: "7px 10px", gap: 8, borderColor: conflict ? "var(--md-error)" : (sel ? "var(--md-outline-variant)" : "var(--md-outline)") },
      },
        React.createElement("span", { className: "material-symbols-rounded", style: { fontSize: 18, color: sel ? "var(--md-primary)" : "var(--md-on-surface-variant)" } }, sel ? "badge" : "add"),
        React.createElement("div", { className: "stack-v", style: { gap: 0, minWidth: 0, flex: 1, textAlign: "left" } },
          sel
            ? React.createElement(React.Fragment, null,
                React.createElement("span", { className: "mono", style: { font: "600 13px/1.2 var(--font-mono)" } }, sel.extId),
                React.createElement("span", { className: "muted", style: { fontSize: 11 } }, sel.name || "—"))
            : React.createElement("span", { className: "muted", style: { fontSize: 13 } }, "Not on device")),
        React.createElement("span", { className: "material-symbols-rounded muted", style: { fontSize: 18 } }, "expand_more")),
      open && pos && React.createElement(React.Fragment, null,
        React.createElement("div", { style: { position: "fixed", inset: 0, zIndex: 60 }, onClick: () => setOpen(false) }),
        React.createElement("div", { className: "menu", style: { position: "fixed", left: pos.left, top: pos.top != null ? pos.top : "auto", bottom: pos.bottom != null ? pos.bottom : "auto", width: pos.width, padding: 0, overflow: "hidden", zIndex: 61 } },
          React.createElement("div", { style: { padding: 10, borderBottom: "1px solid var(--md-outline-variant)" } },
            React.createElement("input", { className: "input", autoFocus: true, placeholder: "Search id or name…", value: q, onChange: (e) => setQ(e.target.value), style: { height: 40, width: "100%" } })),
          React.createElement("div", { style: { maxHeight: pos.maxH, overflowY: "auto", padding: 6 } },
            React.createElement("button", { className: "menu__item", onClick: () => { onChange(null); setOpen(false); } },
              React.createElement("span", { className: "material-symbols-rounded muted" }, "block"),
              React.createElement("span", { className: "muted" }, "Not on this device")),
            filtered.length === 0 && React.createElement("div", { className: "muted", style: { padding: 12, fontSize: 13, textAlign: "center" } }, "No matches"),
            filtered.map((o) => React.createElement("button", { key: o.extId, className: "menu__item", onClick: () => { onChange(o.extId); setOpen(false); }, style: { opacity: (o.takenBy && o.extId !== value) ? 0.6 : 1 } },
              React.createElement("span", { className: "material-symbols-rounded", style: { fontSize: 18, color: "var(--md-on-surface-variant)" } }, "badge"),
              React.createElement("div", { className: "stack-v", style: { gap: 0, flex: 1, minWidth: 0 } },
                React.createElement("span", { className: "mono", style: { fontSize: 13 } }, o.extId),
                React.createElement("span", { className: "muted", style: { fontSize: 11 } }, o.name || "—")),
              o.extId === value
                ? React.createElement("span", { className: "material-symbols-rounded", style: { color: "var(--md-primary)", fontSize: 18 } }, "check")
                : o.takenBy && React.createElement("span", { className: "muted", style: { fontSize: 10, whiteSpace: "nowrap" } }, "→ " + o.takenBy))),
          ),
        ),
      ),
    );
  }

  function DeviceDialog({ entry, onClose, onSave, onDelete, canDelete }) {
    const isNew = !entry.id;
    const [name, setName] = useState(entry.name || "");
    const [location, setLocation] = useState(entry.location || "");
    const [code, setCode] = useState(entry.id || "");
    const idOk = !isNew || /^[A-Za-z0-9_-]+$/.test(code.trim());
    return React.createElement("div", { className: "scrim-bg", onClick: onClose },
      React.createElement("div", { className: "sheet", style: { maxWidth: 460 }, onClick: (e) => e.stopPropagation() },
        React.createElement("div", { className: "sheet__head" },
          React.createElement("div", null,
            React.createElement("div", { className: "eyebrow" }, "Face-recognition terminal"),
            React.createElement("h3", { className: "section-title", style: { fontSize: 20, marginTop: 4 } }, isNew ? "Add device" : "Edit device")),
          React.createElement("button", { className: "icon-btn", onClick: onClose }, React.createElement("span", { className: "material-symbols-rounded" }, "close"))),
        React.createElement("div", { className: "sheet__body" },
          React.createElement("div", { className: "field" },
            React.createElement("label", { className: "field__label" }, "Identifier"),
            isNew
              ? React.createElement("input", { className: "input mono", value: code, onChange: (e) => setCode(e.target.value), placeholder: "e.g. dev-lobby-01", style: idOk ? null : { borderColor: "var(--md-error)" } })
              : React.createElement("div", { className: "row", style: { gap: 8 } },
                  React.createElement("input", { className: "input mono", value: entry.id, readOnly: true, style: { flex: 1, background: "var(--md-surface-container)" } }),
                  React.createElement("button", { className: "icon-btn", title: "Copy ID", onClick: () => { try { navigator.clipboard.writeText(entry.id); } catch (e) {} } },
                    React.createElement("span", { className: "material-symbols-rounded" }, "content_copy"))),
            React.createElement("span", { className: "muted", style: { fontSize: 11.5, marginTop: 2 } }, isNew ? "The identifier used to match this terminal between the Agent and Backend (letters, numbers, - or _)." : "The Agent and Backend use this identifier to sync this terminal's data."),
            !isNew && React.createElement("div", { className: "row", style: { gap: 6, marginTop: 6, alignItems: "baseline" } },
              React.createElement("span", { className: "muted", style: { fontSize: 11.5 } }, "Serial (reported by device)"),
              React.createElement("span", { className: "mono muted", style: { fontSize: 11.5 } }, entry.device_sn || "—"))),
          React.createElement("div", { className: "field" }, React.createElement("label", { className: "field__label" }, "Device name"),
            React.createElement("input", { className: "input", value: name, onChange: (e) => setName(e.target.value), placeholder: "e.g. Front lobby" })),
          React.createElement("div", { className: "field" }, React.createElement("label", { className: "field__label" }, "Location"),
            React.createElement("input", { className: "input", value: location, onChange: (e) => setLocation(e.target.value), placeholder: "e.g. Floor 1 entrance" }))),
        React.createElement("div", { className: "sheet__foot" },
          !isNew && canDelete && React.createElement("button", { className: "btn btn--text", style: { marginRight: "auto", color: "var(--md-error)" }, onClick: () => { onDelete(entry.id); onClose(); } },
            React.createElement("span", { className: "material-symbols-rounded" }, "delete"), "Remove"),
          React.createElement("button", { className: "btn btn--text", onClick: onClose }, "Cancel"),
          React.createElement("button", { className: "btn btn--filled", disabled: !name.trim() || !idOk, onClick: () => { onSave({ id: isNew ? code.trim() : entry.id, name: name.trim(), location: location.trim() }); onClose(); } },
            React.createElement("span", { className: "material-symbols-rounded" }, "check"), "Save device")),
      ),
    );
  }

  function DeviceMappingScreen({ settings, setSettings, onToast }) {
    const [editingDevice, setEditingDevice] = useState(null);
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("all"); // all | unmapped
    const devices = settings.devices || [];
    const faceMap = settings.faceMap || {};
    const enrollments = settings.deviceEnrollments || {};

    // People eligible for device mapping: active employees + pre-added "invited"
    // ones (HR maps their device BEFORE they ever sign in, so their data is
    // attributed when they first log in).
    const MAPPABLE = D.people.filter((p) => p.status === "active" || p.status === "invited");

    // who currently holds a given extId on a device
    const holderOf = (devId, extId) => MAPPABLE.find((p) => faceMap[p.id] && faceMap[p.id][devId] === extId);

    function assign(personId, devId, extId) {
      // The ext_id this employee currently holds on this device (before the change) —
      // needed so the clear path can tell the backend WHICH enrollment to release.
      const prevExtId = (faceMap[personId] && faceMap[personId][devId]) || null;
      const next = JSON.parse(JSON.stringify(faceMap));
      // an extId belongs to one person per device — release it from any current holder
      if (extId) Object.keys(next).forEach((pid) => { if (next[pid] && next[pid][devId] === extId) { delete next[pid][devId]; if (!Object.keys(next[pid]).length) delete next[pid]; } });
      next[personId] = next[personId] || {};
      if (extId) next[personId][devId] = extId; else { delete next[personId][devId]; if (!Object.keys(next[personId]).length) delete next[personId]; }
      setSettings({ ...settings, faceMap: next });
      const who = D.helpers.byId(personId).name.split(" ").slice(-1)[0];
      onToast && onToast(extId ? (who + " → " + extId) : ("Cleared " + who));
      // In API mode, persist the mapping to the backend (PUT /api/devices/map).
      // Fire-and-forget on top of the optimistic update above; toast on failure.
      // Set: map extId -> personId. Clear (extId null): release the ext_id the
      // person was holding by setting its person_id to null.
      if (window.WIDO_USE_API && window.WidoApi) {
        const targetExtId = extId || prevExtId;
        if (targetExtId) {
          window.WidoApi.mapDevice(devId, targetExtId, extId ? personId : null)
            .catch(() => onToast && onToast("Couldn't save mapping — try again"));
        }
      }
    }
    function saveDevice(d) {
      const list = devices.slice();
      const i = list.findIndex((x) => x.id === d.id);
      // Merge so backend-only fields (e.g. device_sn, the serial reported by the
      // agent — HR can't set it here) survive an HR edit of name/location.
      if (i >= 0) list[i] = { ...list[i], ...d }; else list.push(d);
      const enr = Object.assign({}, enrollments); if (!enr[d.id]) enr[d.id] = [];
      setSettings({ ...settings, devices: list, deviceEnrollments: enr });
      onToast && onToast("Device saved · " + d.name);
      // Persist to the backend (PUT /api/devices/<id> upserts) so an HR-added
      // terminal — keyed by the id the agent pushes — survives a reload.
      if (window.WIDO_USE_API && window.WidoApi) {
        window.WidoApi.saveDevice(d.id, { name: d.name, location: d.location })
          .catch(() => onToast && onToast("Couldn't save device — try again"));
      }
    }
    function deleteDevice(id) {
      const next = JSON.parse(JSON.stringify(faceMap));
      Object.keys(next).forEach((pid) => { if (next[pid]) { delete next[pid][id]; if (!Object.keys(next[pid]).length) delete next[pid]; } });
      setSettings({ ...settings, devices: devices.filter((d) => d.id !== id), faceMap: next });
      onToast && onToast("Device removed");
      // Persist removal to the backend (deletes the device + its enrollments).
      if (window.WIDO_USE_API && window.WidoApi) {
        window.WidoApi.deleteDevice(id)
          .catch(() => onToast && onToast("Couldn't remove device — try again"));
      }
    }

    // options per device, flagging who already holds each entry
    function optionsFor(devId) {
      return (enrollments[devId] || []).map((e) => {
        const h = holderOf(devId, e.extId);
        return { extId: e.extId, name: e.name, takenBy: h ? h.name.split(" ").slice(-1)[0] : null };
      });
    }

    const active = MAPPABLE.slice();
    const fullyMapped = active.filter((p) => devices.length && devices.every((d) => faceMap[p.id] && faceMap[p.id][d.id])).length;

    let rows = active.slice();
    if (filter === "unmapped") rows = rows.filter((p) => devices.some((d) => !(faceMap[p.id] && faceMap[p.id][d.id])));
    if (q) rows = rows.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));
    rows.sort((a, b) => a.name.localeCompare(b.name));

    return React.createElement("div", { className: "stack-v", style: { gap: 22 } },
      React.createElement("div", { className: "between wrap" },
        React.createElement("div", null,
          React.createElement("h2", { className: "section-title" }, "Device ID mapping"),
          React.createElement("p", { className: "muted", style: { margin: "4px 0 0" } }, "IDs and names are synced from each terminal. For each employee, pick their entry on each device.")),
        React.createElement("div", { className: "row", style: { gap: 8, flexWrap: "wrap" } },
          React.createElement("span", { className: "tag tag--ok mono" }, fullyMapped + " / " + active.length + " fully mapped"))),

      // devices row
      React.createElement("div", { className: "card card--pad stack-v", style: { gap: 14 } },
        React.createElement("div", { className: "between" },
          React.createElement("div", { className: "eyebrow" }, "Terminals"),
          React.createElement("button", { className: "btn btn--tonal btn--sm", onClick: () => setEditingDevice({}) },
            React.createElement("span", { className: "material-symbols-rounded" }, "add"), "Add device")),
        React.createElement("div", { className: "grid-3" },
          devices.map((d) => React.createElement("div", { key: d.id, className: "card", style: { padding: 14, background: "var(--md-surface-container)", border: "none" } },
            React.createElement("div", { className: "between" },
              React.createElement("div", { className: "row", style: { gap: 10, minWidth: 0 } },
                React.createElement("div", { className: "stat__icon", style: { background: "var(--md-secondary-container)", width: 38, height: 38 } },
                  React.createElement("span", { className: "material-symbols-rounded", style: { color: "var(--md-on-secondary-container)" } }, "face")),
                React.createElement("div", { className: "stack-v", style: { gap: 1, minWidth: 0 } },
                  React.createElement("strong", { style: { fontSize: 14 } }, d.name),
                  React.createElement("span", { className: "muted", style: { fontSize: 12 } }, (enrollments[d.id] || []).length + " enrolled · " + (d.location || "—")))),
              React.createElement("button", { className: "icon-btn", style: { width: 34, height: 34 }, onClick: () => setEditingDevice(d) },
                React.createElement("span", { className: "material-symbols-rounded", style: { fontSize: 18 } }, "edit")))))),
      ),

      // employee mapping table
      React.createElement("div", { className: "stack-v", style: { gap: 14 } },
        React.createElement("div", { className: "between wrap" },
          React.createElement("div", { className: "seg" },
            React.createElement("button", { className: "seg__btn" + (filter === "all" ? " is-on" : ""), onClick: () => setFilter("all") }, "All staff"),
            React.createElement("button", { className: "seg__btn" + (filter === "unmapped" ? " is-on" : ""), onClick: () => setFilter("unmapped") }, "Needs mapping")),
          React.createElement("input", { className: "input", style: { height: 40, width: 220 }, placeholder: "Search name…", value: q, onChange: (e) => setQ(e.target.value) })),
        React.createElement("div", { className: "card tablewrap" },
          React.createElement("table", { className: "data" },
            React.createElement("thead", null, React.createElement("tr", null,
              [React.createElement("th", { key: "emp" }, "Employee")].concat(
                devices.map((d) => React.createElement("th", { key: d.id, style: { minWidth: 180 } }, d.name)),
                [React.createElement("th", { key: "st" }, "Status")]))),
            React.createElement("tbody", null, rows.map((p) => {
              const pm = faceMap[p.id] || {};
              const missing = devices.filter((d) => !pm[d.id]).length;
              return React.createElement("tr", { key: p.id },
                React.createElement("td", null, React.createElement(PersonCell, { person: p })),
                devices.map((d) => React.createElement("td", { key: d.id },
                  React.createElement(EntryPicker, { value: pm[d.id] || null, options: optionsFor(d.id), onChange: (extId) => assign(p.id, d.id, extId) }))),
                React.createElement("td", null, missing === 0
                  ? React.createElement("span", { className: "tag tag--ok" }, React.createElement("span", { className: "material-symbols-rounded" }, "check"), "Mapped")
                  : React.createElement("span", { className: "tag tag--warn" }, missing + " missing")));
            }))),
        ),
        React.createElement("p", { className: "muted", style: { margin: 0, fontSize: 12.5 } },
          "Pick from the IDs each terminal reports — the device name is only a hint. Leave a device blank if the employee isn't enrolled there.")),

      editingDevice && React.createElement(DeviceDialog, { entry: editingDevice, canDelete: devices.length > 1, onClose: () => setEditingDevice(null), onSave: saveDevice, onDelete: deleteDevice }),
    );
  }

  window.DeviceMappingScreen = DeviceMappingScreen;
})();
